import React, { createContext, useContext, useState } from 'react';

type Language = 'en' | 'he';

interface LanguageContextType {
  language: Language;
  toggleLanguage: () => void;
  t: (key: string) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

const translations: Record<string, Record<Language, string>> = {
  "recordingSilence.title": { en: "No sound for one minute", he: "לא זוהה קול במשך דקה" },
  "recordingSilence.message": { en: "No sound has been detected for one minute. Check your microphone, pause or finish the recording.", he: "לא זוהה קול במשך דקה. יש לבדוק את המיקרופון, להשהות או לסיים את ההקלטה." },
  "recordingSilence.dismiss": { en: "Dismiss warning", he: "סגירת האזהרה" },
  "recordingSilence.pause": { en: "Pause", he: "השהיה" },
  "recordingSilence.resume": { en: "Resume", he: "המשך הקלטה" },
  "recordingSilence.finish": { en: "Finish recording", he: "סיום הקלטה" },
  "recordingSilence.paused": { en: "Recording paused", he: "ההקלטה מושהית" },
  "recordingMaxDuration.title": { en: "Recording stopped", he: "ההקלטה הופסקה" },
  "recordingMaxDuration.message": { en: "Maximum length of {minutes} minutes reached. Send it for transcription.", he: "הגעת לאורך המקסימלי של {minutes} דקות. שלחו אותה לתמלול." },
  "recordingMaxDuration.close": { en: "Close", he: "סגירה" },
  "fileTooLarge.title": { en: "File is too large", he: "הקובץ גדול מדי" },
  "fileTooLarge.message": { en: "Split or compress the file before uploading.", he: "פצל או כווץ את הקובץ לפני ההעלאה." },
  "fileTooLarge.close": { en: "Close", he: "סגירה" },
  "pocketMode.title": { en: "Pocket mode — recording", he: "מצב כיס — מקליט" },
  "pocketMode.recording": { en: "Recording…", he: "מקליט…" },
  "pocketMode.hint": { en: "Don't lock the phone — locking stops the recording. You can put it down face up or in your pocket.", he: "אין לנעול את הטלפון — נעילה עוצרת את ההקלטה. אפשר להניח אותו או לשים בכיס." },
  "pocketMode.unlock": { en: "Swipe to exit", he: "החליקו ליציאה" },
  // Navbar
  'nav.home': { en: 'Home', he: 'בית' },
  'nav.about': { en: 'About', he: 'אודות' },
  'nav.products': { en: 'Products', he: 'מוצרים' },
  'nav.lawFirms': { en: 'For Law Firms', he: 'למשרדי עורכי דין' },
  'nav.why': { en: 'Why Legalix', he: 'למה ליגליקס' },
  'nav.contact': { en: 'Contact', he: 'צור קשר' },

  // Hero
  'hero.title': { en: 'The First All-in-One Legal Intelligence Platform', he: 'פלטפורמת הבינה המשפטית הכוללת הראשונה' },
  'hero.subtitle': { en: 'From the first conversation to the perfect legal document', he: 'מהשיחה הראשונה ועד המסמך המשפטי המושלם' },
  'hero.cta': { en: 'Our Modules', he: 'המודלים שלנו' },
  'hero.learnMore': { en: 'About Us', he: 'מי אנחנו?' },

  // Features
  'features.title': { en: 'Our Modules', he: 'המודולים שלנו' },
  'features.subtitle': { en: 'Six powerful modules working together to transform your legal workflow', he: 'שישה מודולים עוצמתיים העובדים יחד כדי לשנות את זרימת העבודה המשפטית שלך' },
  'features.inDevelopment': { en: 'In Development', he: 'בפיתוח' },
  'features.tryNow': { en: 'Try Now', he: 'נסו עכשיו' },
  'features.requestDemo': { en: 'Request Demo', he: 'בקש הדגמה' },
  'features.whatsappMessage': { en: 'Hi Ali! I would like to request a demo for {featureName}', he: 'הי אלי! {featureName} נשמע ממש מעניין, אני רוצה להזמין הדגמה של זה בבקשה' },

  'features.voice.title': { en: 'Legalix Voice', he: 'Legalix Voice' },
  'features.voice.subtitle': { en: 'From Conversation to Case File', he: 'מהשיחה אל התיק' },
  'features.voice.desc': { en: 'Records and transcribes client meetings in real-time, automatically extracting legal facts, claims, key events, and significant evidence.', he: 'מקליטה ומתמללת פגישות עם לקוחות בזמן אמת, מחלצת אוטומטית עובדות משפטיות, עילות, אירועים מרכזיים וראיות משמעותיות.' },

  'features.search.title': { en: 'Legalix Search', he: 'Legalix Search' },
  'features.search.subtitle': { en: 'Intelligent Search Across Documents', he: 'חיפוש חכם במסמכי המשרד' },
  'features.search.desc': { en: 'Search by document content, not just file names. Semantic analysis understands legal meaning and returns results based on context.', he: 'חיפוש לפי תוכן המסמך, ולא רק לפי שם קובץ. ניתוח סמנטי מבין את המשמעות המשפטית ומחזיר תוצאות לפי הקשר.' },

  'features.case.title': { en: 'Legalix Case', he: 'Legalix Case' },
  'features.case.subtitle': { en: 'Intelligent Case Analysis', he: 'שיחה חכמה עם התיק' },
  'features.case.desc': { en: 'Chat with your entire case file. Ask questions about events, evidence, claims, and contradictions. The system identifies gaps and unverified facts.', he: 'שיחה עם כלל התיק. שאל שאלות על אירועים, ראיות, טענות וסתירות. המערכת מזהה חוסרים ועובדות לא מאומתות.' },

  'features.doc.title': { en: 'Legalix Doc', he: 'Legalix Doc' },
  'features.doc.subtitle': { en: 'Intelligent Document Creation', he: 'יצירת מסמכים משפטיים חכמים' },
  'features.doc.desc': { en: 'Automatically generates legal documents based on case analysis: pleadings, contracts, affidavits, and briefs. Writes like a senior attorney.', he: 'מייצרת אוטומטית מסמכים משפטיים מבוססי ניתוח התיק: כתבי טענות, חוזים, תצהירים וסיכומים. כותבת כמו עורך דין בכיר.' },

  'features.analyzer.title': { en: 'Legalix Analyzer', he: 'Legalix Analyzer' },
  'features.analyzer.subtitle': { en: 'Quality Control & Strategic Feedback', he: 'בקרת איכות ומשוב אסטרטגי' },
  'features.analyzer.desc': { en: 'Multi-layered review: completeness, logic, strategy, and language. Suggests strategic rewrites, not just linguistic corrections.', he: 'בדיקה רב-שכבתית: שלמות, לוגיקה, אסטרטגיה ושפה. ממליץ על שכתוב אסטרטגי, לא רק תיקונים לשוניים.' },

  'features.ai.title': { en: 'Legalix AI', he: 'Legalix AI' },
  'features.ai.subtitle': { en: 'The Legal Agent', he: 'הסוכן המשפטי' },
  'features.ai.desc': { en: 'Massive knowledge base built on current case law, statutes, and regulations. Ensures every legal argument stands on solid ground.', he: 'מאגר ידע עצום המבוסס על פסיקה, חוקים ותקנות עדכניים. מוודא שכל טיעון משפטי עומד על בסיס איתן.' },

  // About
  'about.title': { en: 'Built by Lawyers, for Lawyers', he: 'נבנתה על ידי עורכי דין, עבור עורכי דין' },
  'about.subtitle': { en: 'Transforming Legal Work from the Ground Up', he: 'משנה את העבודה המשפטית מהיסוד' },
  'about.p1': { en: 'Legalix.ai was born from a simple understanding: lawyers need tools that think like they do. Not just automation, but intelligence. Not just efficiency, but excellence.', he: 'Legalix.ai נולדה מהבנה פשוטה: עורכי דין צריכים כלים שחושבים כמוהם. לא רק אוטומציה, אלא אינטליגנציה. לא רק יעילות, אלא מצוינות.' },
  'about.p2': { en: 'We connect six intelligent modules into one seamless workflow. Each module enhances the others, creating a system that doesn\'t just assist lawyers—it thinks alongside them.', he: 'אנחנו מחברים שישה מודולים חכמים לתוך זרימת עבודה אחת חלקה. כל מודול משפר את האחרים, יוצר מערכת שלא רק מסייעת לעורכי דין—היא חושבת יחד איתם.' },
  'about.vision': { en: 'Our Vision', he: 'החזון שלנו' },
  'about.vision.desc': { en: 'Transform every lawyer into a complete team. Transform every case into accessible legal knowledge. Transform legal work itself.', he: 'להפוך כל עורך דין לצוות שלם. להפוך כל תיק לידע משפטי נגיש. להפוך את העבודה המשפטית עצמה.' },

  // Testimonials
  'testimonials.title': { en: 'What Legal Professionals Say', he: 'מה אומרים אנשי משפט' },
  'testimonials.subtitle': { en: 'Trusted by leading law firms and legal departments', he: 'מהימן על ידי משרדי עורכי דין ומחלקות משפטיות מובילות' },

  // Pricing
  'pricing.title': { en: 'Flexible Plans for Every Need', he: 'תוכניות גמישות לכל צורך' },
  'pricing.subtitle': { en: 'Choose the plan that fits your practice', he: 'בחר את התוכנית שמתאימה לך' },
  'pricing.solo.name': { en: 'Solo Practitioner', he: 'עורך דין עצמאי' },
  'pricing.solo.desc': { en: 'Perfect for individual lawyers', he: 'מושלם לעורכי דין יחידים' },
  'pricing.solo.feature1': { en: 'All 6 modules included', he: 'כל 6 המודולים כלולים' },
  'pricing.solo.feature2': { en: 'Up to 50 cases per month', he: 'עד 50 תיקים בחודש' },
  'pricing.solo.feature3': { en: 'Voice transcription (10 hours)', he: 'תמלול שיחות (10 שעות)' },
  'pricing.solo.feature4': { en: 'Email support', he: 'תמיכה במייל' },
  'pricing.solo.feature5': { en: 'Israeli law database', he: 'מאגר דין ישראלי' },
  'pricing.solo.cta': { en: 'Start Trial', he: 'התחל ניסיון' },
  
  'pricing.firm.name': { en: 'Law Firm', he: 'משרד עורכי דין' },
  'pricing.firm.desc': { en: 'For growing legal teams', he: 'לצוותים משפטיים צומחים' },
  'pricing.firm.feature1': { en: 'Everything in Solo', he: 'כל מה שב-עצמאי' },
  'pricing.firm.feature2': { en: 'Up to 200 cases per month', he: 'עד 200 תיקים בחודש' },
  'pricing.firm.feature3': { en: 'Voice transcription (50 hours)', he: 'תמלול שיחות (50 שעות)' },
  'pricing.firm.feature4': { en: 'Priority support', he: 'תמיכה עדיפה' },
  'pricing.firm.feature5': { en: 'Team collaboration tools', he: 'כלי שיתוף פעולה לצוות' },
  'pricing.firm.feature6': { en: 'Advanced analytics', he: 'אנליטיקה מתקדמת' },
  'pricing.firm.badge': { en: 'Most Popular', he: 'הכי פופולרי' },
  'pricing.firm.cta': { en: 'Start Trial', he: 'התחל ניסיון' },
  
  'pricing.enterprise.name': { en: 'Enterprise', he: 'ארגוני' },
  'pricing.enterprise.desc': { en: 'For large firms and legal departments', he: 'למשרדים גדולים ומחלקות משפטיות' },
  'pricing.enterprise.feature1': { en: 'Everything in Firm', he: 'כל מה שב-משרד' },
  'pricing.enterprise.feature2': { en: 'Unlimited cases', he: 'תיקים ללא הגבלה' },
  'pricing.enterprise.feature3': { en: 'Unlimited voice transcription', he: 'תמלול ללא הגבלה' },
  'pricing.enterprise.feature4': { en: 'Dedicated account manager', he: 'מנהל חשבון ייעודי' },
  'pricing.enterprise.feature5': { en: 'Custom integrations', he: 'אינטגרציות מותאמות' },
  'pricing.enterprise.feature6': { en: 'On-premise deployment option', he: 'אפשרות התקנה מקומית' },
  'pricing.enterprise.feature7': { en: 'Advanced security features', he: 'תכונות אבטחה מתקדמות' },
  'pricing.enterprise.cta': { en: 'Contact Sales', he: 'צור קשר עם מכירות' },

  // FAQ
  'faq.title': { en: 'Frequently Asked Questions', he: 'שאלות נפוצות' },
  'faq.subtitle': { en: 'Everything you need to know about Legalix.ai', he: 'כל מה שאתה צריך לדעת על Legalix.ai' },
  'faq.q1': { en: 'How does Legalix.ai ensure data privacy and security?', he: 'איך Legalix.ai מבטיחה פרטיות ואבטחת מידע?' },
  'faq.a1': { en: 'We implement bank-level encryption, secure cloud storage, and comply with all Israeli privacy regulations. Your client data never leaves your control, and we never use your data to train our models. Enterprise clients can choose on-premise deployment for maximum security.', he: 'אנו מיישמים הצפנה ברמה בנקאית, אחסון ענן מאובטח ועומדים בכל תקנות הפרטיות הישראליות. נתוני הלקוחות שלך לעולם לא יוצאים מהשליטה שלך, ואנחנו אף פעם לא משתמשים בנתונים שלך לאימון המודלים שלנו. לקוחות ארגוניים יכולים לבחור בהתקנה מקומית לאבטחה מקסימלית.' },
  
  'faq.q2': { en: 'Does Legalix.ai support Israeli law?', he: 'האם Legalix.ai תומכת בדין הישראלי?' },
  'faq.a2': { en: 'Yes! Legalix AI is built on a comprehensive database of Israeli case law, statutes, and regulations. The system is updated regularly to reflect the latest legal developments and court rulings.', he: 'כן! Legalix AI בנויה על מאגר מקיף של פסיקה, חוקים ותקנות ישראליים. המערכת מתעדכנת באופן קבוע כדי לשקף את ההתפתחויות המשפטיות האחרונות ופסקי הדין העדכניים.' },
  
  'faq.q3': { en: 'Can junior lawyers use Legalix effectively?', he: 'האם עורכי דין צעירים יכולים להשתמש ב-Legalix ביעילות?' },
  'faq.a3': { en: 'Absolutely. Legalix is designed to elevate junior lawyers to the level of senior practitioners. The system guides them through proper legal reasoning, document structure, and strategic thinking—effectively serving as a mentor that\'s available 24/7.', he: 'בהחלט. Legalix מתוכננת להעלות עורכי דין צעירים לרמה של עורכי דין בכירים. המערכת מדריכה אותם דרך חשיבה משפטית נכונה, מבנה מסמכים ואסטרטגיה—למעשה משמשת כמנטור זמין 24/7.' },
  
  'faq.q4': { en: 'How accurate is the voice transcription?', he: 'מה רמת הדיוק של תמלול השיחות?' },
  'faq.a4': { en: 'Legalix Voice achieves over 95% accuracy in Hebrew and English, even with legal terminology. The system learns your firm\'s specific vocabulary over time, improving accuracy further.', he: 'Legalix Voice משיגה דיוק של מעל 95% בעברית ובאנגלית, גם עם טרמינולוגיה משפטית. המערכת לומדת את האוצר המילים הספציפי של המשרד שלך לאורך זמן, ומשפרת את הדיוק עוד יותר.' },
  
  'faq.q5': { en: 'Can I integrate Legalix with my existing tools?', he: 'האם אני יכול לשלב את Legalix עם הכלים הקיימים שלי?' },
  'faq.a5': { en: 'Yes. Legalix integrates with popular case management systems, document management platforms, and calendar tools. Enterprise plans include custom integration support for your specific workflow.', he: 'כן. Legalix משתלבת עם מערכות ניהול תיקים פופולריות, פלטפורמות ניהול מסמכים וכלי לוח שנה. תוכניות ארגוניות כוללות תמיכה באינטגרציה מותאמת לזרימת העבודה הספציפית שלך.' },
  
  'faq.q6': { en: 'What happens to my data if I cancel?', he: 'מה קורה לנתונים שלי אם אבטל?' },
  'faq.a6': { en: 'You can export all your data in standard formats before cancellation. After cancellation, we retain your data for 30 days in case you want to reactivate, then permanently delete it from our systems.', he: 'אתה יכול לייצא את כל הנתונים שלך בפורמטים סטנדרטיים לפני הביטול. לאחר הביטול, אנו שומרים את הנתונים שלך למשך 30 יום למקרה שתרצה להפעיל מחדש, ואז מוחקים אותם לצמיתות מהמערכות שלנו.' },

  // Contact
  'contact.title': { en: 'Get in Touch', he: 'צור קשר' },
  'contact.subtitle': { en: 'Request a demo or ask us anything', he: 'בקש הדגמה או שאל אותנו כל דבר' },
  'contact.name': { en: 'Your Name', he: 'שם מלא' },
  'contact.email': { en: 'Email Address', he: 'כתובת אימייל' },
  'contact.message': { en: 'Your Message', he: 'ההודעה שלך' },
  'contact.submit': { en: 'Send Message', he: 'שלח הודעה' },
  'contact.demo': { en: 'Or schedule a demo', he: 'או קבע הדגמה' },
  'contact.quickActions': { en: 'Quick Contact', he: 'יצירת קשר מהירה' },
  'contact.callUs': { en: 'Call Us', he: 'התקשר אלינו' },
  'contact.emailUs': { en: 'Email Us', he: 'שלח מייל' },
  'contact.whatsapp': { en: 'WhatsApp', he: 'וואטסאפ' },
  'contact.getDirections': { en: 'Get Directions', he: 'נווט למשרד' },
  'contact.ourLocation': { en: 'Our Location', he: 'המיקום שלנו' },
  'contact.formTitle': { en: 'Send Us a Message', he: 'שלח לנו הודעה' },

  // Footer
  'footer.tagline': { en: 'The All-in-One Legal Intelligence Platform', he: 'פלטפורמת הבינה המשפטית הכוללת' },
  'footer.products': { en: 'Products', he: 'מוצרים' },
  'footer.company': { en: 'Company', he: 'חברה' },
  'footer.legal': { en: 'Legal', he: 'משפטי' },
  'footer.privacy': { en: 'Privacy Policy', he: 'מדיניות פרטיות' },
  'footer.terms': { en: 'Terms of Service', he: 'תנאי שימוש' },
  'footer.signIn': { en: 'Sign In', he: 'כניסה למערכת' },
  'footer.copyright': { en: '© 2025 Legalix.ai. All rights reserved.', he: '© 2025 Legalix.ai. כל הזכויות שמורות.' },
  'footer.builtBy': { en: 'Built by', he: 'נבנה על ידי' },

  // Law Firms Section
  'lawfirms.title': { en: 'For Every Type of Practice', he: 'לכל סוג של משרד' },
  'lawfirms.subtitle': { en: 'Whether you\'re solo or leading a large firm, Legalix scales with you', he: 'בין אם אתה עצמאי או מוביל משרד גדול, Legalix גדלה איתך' },

  // Why Section
  'why.title': { en: 'Why Choose Legalix', he: 'למה לבחור ב-Legalix' },
  'why.subtitle': { en: 'The competitive advantages that set us apart', he: 'היתרונות התחרותיים שמייחדים אותנו' },

  // Terms of Service Page
  'tos.title': { en: 'Terms of Service', he: 'תנאי שימוש' },
  'tos.lastUpdated': { en: 'Last updated:', he: 'עודכן לאחרונה:' },
  'tos.loading': { en: 'Loading Terms of Service...', he: 'טוען תנאי שימוש...' },
  'tos.error': { en: 'Failed to load Terms of Service. Please try again later.', he: 'טעינת תנאי השימוש נכשלה. אנא נסה שוב מאוחר יותר.' },
  'tos.downloadPdf': { en: 'Download PDF', he: 'הורד PDF' },
  'tos.downloadPdfEn': { en: 'Download English PDF', he: 'הורד PDF באנגלית' },
  'tos.downloadPdfHe': { en: 'Download Hebrew PDF', he: 'הורד PDF בעברית' },

  // Privacy Policy Page
  'privacy.title': { en: 'Privacy Policy', he: 'מדיניות פרטיות' },
  'privacy.lastUpdated': { en: 'Last updated:', he: 'עודכן לאחרונה:' },
  'privacy.loading': { en: 'Loading Privacy Policy...', he: 'טוען מדיניות פרטיות...' },
  'privacy.error': { en: 'Failed to load Privacy Policy. Please try again later.', he: 'טעינת מדיניות הפרטיות נכשלה. אנא נסה שוב מאוחר יותר.' },
};

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [language, setLanguage] = useState<Language>('he');

  const toggleLanguage = () => {
    setLanguage(prev => prev === 'en' ? 'he' : 'en');
  };

  const t = (key: string): string => {
    return translations[key]?.[language] || key;
  };

  return (
    <LanguageContext.Provider value={{ language, toggleLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = (): LanguageContextType => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

